// INVARIANT: Dev mode accelerates reality — it does not replace it (see docs/invariants/PHASE_3_INVARIANTS.md)
import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { debug } from '@/utils/debug';

const DEV_MODE_KEY = 'studyforge_dev_mode';

interface DeveloperModeContextType {
  isDevMode: boolean;
  toggleDevMode: () => void;
  isScreenshotMode: boolean;
  toggleScreenshotMode: () => void;
}

const DeveloperModeContext = createContext<DeveloperModeContextType | undefined>(undefined);

export function DeveloperModeProvider({ children }: { children: ReactNode }) {
  const [isDevMode, setIsDevMode] = useState(() => {
    return localStorage.getItem(DEV_MODE_KEY) === 'true';
  });
  const [isScreenshotMode, setIsScreenshotMode] = useState(false);

  useEffect(() => {
    localStorage.setItem(DEV_MODE_KEY, isDevMode.toString());
  }, [isDevMode]);

  const toggleDevMode = () => {
    const newValue = !isDevMode;
    setIsDevMode(newValue);
    debug.log('[Dev] Developer Mode toggled:', newValue ? 'ON' : 'OFF');
  };

  const toggleScreenshotMode = useCallback(() => {
    // Only require dev mode to be on
    if (!isDevMode) return;
    
    const newValue = !isScreenshotMode;
    setIsScreenshotMode(newValue);
    debug.log('[Dev] Screenshot Mode toggled:', newValue ? 'ON (UI hidden)' : 'OFF');
  }, [isDevMode, isScreenshotMode]);

  // Auto-timeout: screenshot mode auto-disables after 30 seconds
  useEffect(() => {
    if (!isScreenshotMode) return;
    
    const timeout = setTimeout(() => {
      setIsScreenshotMode(false);
      debug.log('[Dev] Screenshot Mode auto-disabled after 30s');
    }, 30000);
    
    return () => clearTimeout(timeout);
  }, [isScreenshotMode]);

  // Triple-tap detection in bottom-left corner to restore
  useEffect(() => {
    if (!isScreenshotMode) return;
    
    let tapCount = 0;
    let tapTimeout: ReturnType<typeof setTimeout>;
    
    const handleTap = (e: MouseEvent | TouchEvent) => {
      const x = 'touches' in e ? e.touches[0]?.clientX ?? 0 : e.clientX;
      const y = 'touches' in e ? e.touches[0]?.clientY ?? 0 : e.clientY;
      
      // Bottom-left corner (50px x 50px hotzone)
      const isBottomLeft = x < 50 && y > window.innerHeight - 50;
      
      if (isBottomLeft) {
        tapCount++;
        clearTimeout(tapTimeout);
        
        if (tapCount >= 3) {
          setIsScreenshotMode(false);
          debug.log('[Dev] Screenshot Mode restored via triple-tap');
          tapCount = 0;
        } else {
          tapTimeout = setTimeout(() => { tapCount = 0; }, 500);
        }
      }
    };
    
    window.addEventListener('click', handleTap);
    window.addEventListener('touchstart', handleTap);
    
    return () => {
      window.removeEventListener('click', handleTap);
      window.removeEventListener('touchstart', handleTap);
      clearTimeout(tapTimeout);
    };
  }, [isScreenshotMode]);

  // Keyboard shortcut: Ctrl/Cmd + Shift + H
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'h') {
        e.preventDefault();
        toggleScreenshotMode();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleScreenshotMode]);

  return (
    <DeveloperModeContext.Provider value={{ isDevMode, toggleDevMode, isScreenshotMode, toggleScreenshotMode }}>
      {children}
    </DeveloperModeContext.Provider>
  );
}

export function useDeveloperMode() {
  const context = useContext(DeveloperModeContext);
  if (!context) {
    throw new Error('useDeveloperMode must be used within DeveloperModeProvider');
  }
  return context;
}
